import { useFragment, useMutation } from '@apollo/client/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { EscrowMembershipFragment } from 'src/graphql/actions/escrowMemberships/escrowMemberships.fragment';
import { EscrowMembershipUpdateDocument } from 'src/graphql/actions/escrowMemberships/escrowMembershipUpdate.mutation';
import { EscrowMembershipRoleEnum, type IEscrowMembership } from 'src/graphql/schema';
import { useMembershipActions } from 'src/containers/Application/Memberships/View';

import { roleOptionsDict } from '../../Management/model/roleOptions';
import { permissionOptions } from '../model';

const schema = z.object({
  role: z.nativeEnum(EscrowMembershipRoleEnum),
});

type IFormValues = z.infer<typeof schema>;

type FormProps = {
  membershipId: string;
  role: EscrowMembershipRoleEnum;
  userName?: string | null;
};

const EditPermissionForm = ({ membershipId, role, userName }: FormProps) => {
  const { closeEditDialog } = useMembershipActions();
  const [updateEscrowMembership, { loading }] = useMutation(EscrowMembershipUpdateDocument);

  const form = useForm<IFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role },
  });

  const onSave = async ({ role }: IFormValues) => {
    try {
      await updateEscrowMembership({
        variables: {
          input: {
            escrowMembershipId: membershipId,
            escrowMembershipParams: { role },
          },
        },
      });

      closeEditDialog();
      toast.success(`${userName}'s permission is now ${roleOptionsDict[role]}`);
    } catch {
      toast.error('Failed to update member access');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSave)} className="space-y-2">
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <Label className="font-semibold text-slate-700">Permission</Label>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange}>
                  {permissionOptions.map((option) => (
                    <div key={option.value} className="flex items-start gap-2">
                      <RadioGroupItem value={option.value} id={`${membershipId}-${option.value}`} />
                      <Label htmlFor={`${membershipId}-${option.value}`} className="space-y-1.5">
                        <span className="block font-bold">{option.label}</span>
                        <p className="text-xs text-muted-foreground font-normal">{option.description}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={loading} isLoading={loading}>
            Save permission
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

const EditPermissionDialog = () => {
  const { editMembershipId, closeEditDialog } = useMembershipActions();

  const membership = useFragment<IEscrowMembership>({
    fragment: EscrowMembershipFragment,
    from: { __typename: 'EscrowMembership', id: editMembershipId ?? '' },
  });

  if (!membership) return null;

  const userName = membership.data.appSubscription?.user?.name;

  const onOpenChange = (open: boolean) => {
    if (!open) closeEditDialog();
  };

  return (
    <Dialog open={Boolean(editMembershipId)} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit {userName}&apos;s permission</DialogTitle>
        </DialogHeader>
        {editMembershipId && (
          <EditPermissionForm
            key={editMembershipId}
            membershipId={editMembershipId}
            role={membership.data.role ?? EscrowMembershipRoleEnum.Viewer}
            userName={userName}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditPermissionDialog;
